import React, { useState, useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import AnnouncementBanner from '../common/AnnouncementBanner';
import TelegramLinkBanner from '../common/TelegramLinkBanner';
import FloatingCommunityButtons from '../common/FloatingCommunityButtons';
import ChatWidget from '../common/ChatWidget';
import WelcomeModal from '../WelcomeModal';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../contexts/ThemeContext';
import { COMMUNITY_LINKS } from '../../utils/constants';

const WHATSAPP_LINK = COMMUNITY_LINKS.WHATSAPP;
const TELEGRAM_BOT_LINK = 'https://t.me/GHDM_Notifications_bot';

const LEGAL_PATHS = ['/terms', '/privacy'];

const Layout: React.FC = () => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const { user, isAuthenticated } = useAuth();
  const { theme } = useTheme();

  const showSidebar = Boolean(isAuthenticated && user);

  // Redirect /terms and /privacy to NotFoundPage for authenticated users inside the layout
  const isLegalRedirect = LEGAL_PATHS.includes(location.pathname);

  // Show welcome modal for new logins
  useEffect(() => {
    if (!isLegalRedirect && isAuthenticated && user) {
      const welcomeShown = sessionStorage.getItem('welcomeModalShown');
      if (!welcomeShown) {
        const timer = setTimeout(() => {
          setShowWelcomeModal(true);
          sessionStorage.setItem('welcomeModalShown', 'true');
        }, 1000);
        return () => clearTimeout(timer);
      }
    }
  }, [isAuthenticated, user, isLegalRedirect]);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    if (!isLegalRedirect && window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  }, [location.pathname, isLegalRedirect]);

  // Prevent background scrolling while the mobile sidebar is open
  useEffect(() => {
    if (!sidebarOpen || isLegalRedirect) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [sidebarOpen, isLegalRedirect]);

  // Handle window resize
  useEffect(() => {
    if (isLegalRedirect) return;

    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isLegalRedirect]);

  // Redirect /terms and /privacy to NotFoundPage for authenticated users inside the layout
  if (isLegalRedirect) {
    return <Navigate to="/404" replace />;
  }

  return (
    <div className={`min-h-screen bg-[#f8faf9] dark:bg-navy-900 ${theme} overflow-x-hidden`}>
      {/* Sidebar */}
      {showSidebar && (
        <Sidebar
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          user={user}
        />
      )}

      {/* Main content with responsive sidebar spacing */}
      <div className={`${showSidebar ? 'lg:pl-56 xl:pl-64 ' : ''}flex flex-col min-h-screen w-full max-w-full overflow-x-hidden`}>
        {/* Sticky container for banner + header */}
        <div className="sticky top-0 z-40 flex flex-col w-full">
          {/* Announcement Banner */}
          <AnnouncementBanner />
          
          {/* Telegram Link Banner */}
          {isAuthenticated && <TelegramLinkBanner />}

          {/* Header */}
          <Header
            onMenuClick={showSidebar ? () => setSidebarOpen(true) : undefined}
            user={user}
          />
        </div>

        {/* Page content with responsive padding */}
        <main className="flex-1 py-3 sm:py-4 lg:py-6 overflow-x-hidden">
          <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 w-full">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Floating WhatsApp + Telegram buttons */}
      <FloatingCommunityButtons showTelegramLink />

      {/* AI Chat Assistant */}
      <ChatWidget />

      {/* Welcome Modal - shows on first login */}
      <WelcomeModal
        isOpen={showWelcomeModal}
        onClose={() => setShowWelcomeModal(false)}
        userName={user?.profile?.firstName || user?.username || 'there'}
        whatsappLink={WHATSAPP_LINK}
        telegramLink={TELEGRAM_BOT_LINK}
      />
    </div>
  );
};

export default Layout;
